'use client';

import { useState } from 'react';
import { X, Shield, Trash2, Download, Eye, EyeOff } from 'lucide-react';
import { trackEvent } from './analytics';
import { clearConversation } from './conversationStorage';
import { resetOnboarding } from './Onboarding';
import { resetPrivacy } from './PrivacyBanner';

export interface PrivacySettingsProps {
  onClose: () => void;
}

export function PrivacySettings({ onClose }: PrivacySettingsProps) {
  const [isExiting, setIsExiting] = useState(false);
  const [showDataPreview, setShowDataPreview] = useState(false);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleClearAllData = () => {
    if (confirm('Are you sure you want to delete all your data? This cannot be undone.')) {
      clearConversation();
      resetOnboarding();
      resetPrivacy();
      localStorage.removeItem('dreambot_theme');
      localStorage.removeItem('dreambot_language');
      trackEvent('data_cleared', { source: 'privacy_settings' });
      alert('All data has been cleared from your device.');
      handleClose();
    }
  };

  const handleExportData = () => {
    try {
      const data: Record<string, any> = {};
      
      // Collect all DreamBot data
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('dreambot_')) {
          data[key] = localStorage.getItem(key);
        }
      }

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dreambot-data-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      trackEvent('data_exported');
      alert('Your data has been downloaded.');
    } catch (error) {
      alert('Failed to export data. Please try again.');
    }
  };

  const getStoredDataSummary = () => {
    const items: string[] = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('dreambot_')) {
          const value = localStorage.getItem(key);
          const size = new Blob([value || '']).size;
          items.push(`${key}: ${(size / 1024).toFixed(2)} KB`);
        }
      }
    } catch {
      return ['Unable to access storage'];
    }
    return items.length > 0 ? items : ['No data stored'];
  };

  return (
    <div
      className={`
        absolute inset-0 z-50 bg-card flex flex-col
        transition-opacity duration-300
        ${isExiting ? 'opacity-0' : 'opacity-100'}
      `}
    >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-border/50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
              <Shield size={20} className="text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-[17px] leading-[1.2] tracking-[-0.02em] font-semibold text-foreground">Privacy & Data</h2>
          </div>
          <button
            onClick={handleClose}
            aria-label="Close privacy settings"
            className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-muted hover:bg-muted/80 transition-colors"
          >
            <X size={16} className="text-foreground" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {/* Data Storage Info */}
          <div className="space-y-3">
            <h3 className="text-[13px] font-bold uppercase tracking-wider text-muted-foreground">What We Store</h3>
            <div className="bg-muted rounded-xl p-4 space-y-2">
              <p className="text-xs text-muted-foreground leading-relaxed">
                <strong className="text-foreground">Locally on your device:</strong>
              </p>
              <ul className="text-xs text-muted-foreground space-y-1 ml-4">
                <li>• Your conversation history (48 hours)</li>
                <li>• Theme and language preferences</li>
                <li>• Onboarding completion status</li>
                <li>• Privacy acceptance status</li>
              </ul>
              <p className="text-xs text-muted-foreground leading-relaxed mt-3">
                <strong className="text-foreground">Not stored:</strong> Personal information, payment details, or any data shared with third parties.
              </p>
            </div>
          </div>

          {/* Data Preview */}
          <div className="space-y-3">
            <button
              onClick={() => setShowDataPreview(!showDataPreview)}
              className="flex items-center gap-2 text-[13px] font-semibold text-foreground hover:text-muted-foreground transition-colors"
            >
              {showDataPreview ? <EyeOff size={16} /> : <Eye size={16} />}
              {showDataPreview ? 'Hide' : 'View'} Stored Data
            </button>
            
            {showDataPreview && (
              <div className="bg-muted rounded-xl p-4 max-h-40 overflow-y-auto">
                <div className="space-y-1">
                  {getStoredDataSummary().map((item, index) => (
                    <p key={index} className="text-xs font-mono text-muted-foreground">
                      {item}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <h3 className="text-[13px] font-bold uppercase tracking-wider text-muted-foreground">Your Rights</h3>
            
            <button
              onClick={handleExportData}
              className="w-full flex items-center gap-3 p-4 bg-muted rounded-xl hover:bg-muted/80 transition-colors text-left group"
            >
              <Download size={20} className="text-blue-600 dark:text-blue-400" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">Export Your Data</p>
                <p className="text-xs text-muted-foreground">Download all stored data as JSON</p>
              </div>
            </button>

            <button
              onClick={handleClearAllData}
              className="w-full flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors text-left group"
            >
              <Trash2 size={20} className="text-red-600 dark:text-red-400" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-600 dark:text-red-400">Delete All Data</p>
                <p className="text-xs text-red-600/70 dark:text-red-400/70">Permanently remove all stored information</p>
              </div>
            </button>
          </div>

          {/* Trust Signals */}
          <div className="pt-4 border-t border-border/50">
            <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Shield size={12} />
                Secure
              </span>
              <span>•</span>
              <span>GDPR Compliant</span>
              <span>•</span>
              <span>No Tracking</span>
            </div>
          </div>
        </div>
    </div>
  );
}
